import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Minus, Plus, ShoppingBag, Trash2, PawPrint } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatBRL } from "@/lib/format";
import { toast } from "sonner";
import { scrollToId } from "@/lib/scroll";

export default function CartDrawer() {
    const { items, setQty, remove, clear, subtotal, count, open, setOpen } = useCart();

    const checkout = () => {
        toast.info("Demonstração: o checkout não está disponível", {
            description: "Este é um site de portfólio — nenhum pagamento é processado.",
        });
    };

    const explore = () => {
        setOpen(false);
        setTimeout(() => scrollToId("catalogo"), 80);
    };

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent
                side="right"
                data-testid="cart-drawer"
                className="flex w-full flex-col gap-0 border-l border-sand bg-cream p-0 sm:max-w-md"
            >
                <SheetHeader className="border-b border-sand/70 px-6 pb-4 pt-6">
                    <SheetTitle className="flex items-center gap-2 font-serif text-xl font-bold text-forest">
                        <ShoppingBag className="h-5 w-5 text-coral" />
                        Sua cesta demo
                        {count > 0 && (
                            <span className="rounded-full bg-coral/20 px-2.5 py-0.5 text-xs font-extrabold text-coral-dark">
                                {count}
                            </span>
                        )}
                    </SheetTitle>
                    <SheetDescription className="text-xs text-graphite-muted">
                        Carrinho demonstrativo — os itens ficam salvos apenas no seu navegador.
                    </SheetDescription>
                </SheetHeader>

                {items.length === 0 ? (
                    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
                        <span className="grid h-20 w-20 place-items-center rounded-full bg-coral/15">
                            <PawPrint className="h-9 w-9 text-coral" />
                        </span>
                        <p className="font-serif text-xl font-bold text-forest">Sua cesta está vazia</p>
                        <p className="max-w-[240px] text-sm text-graphite-muted">
                            Adicione produtos demo do catálogo para vê-los aqui.
                        </p>
                        <button
                            type="button"
                            data-testid="cart-empty-explore-button"
                            onClick={explore}
                            className="mt-2 rounded-full bg-forest px-6 py-3 text-sm font-bold text-cream transition-all hover:bg-forest-light"
                        >
                            Explorar catálogo
                        </button>
                    </div>
                ) : (
                    <>
                        <ul className="flex-1 divide-y divide-sand/60 overflow-y-auto px-6" data-testid="cart-items-list">
                            {items.map((item) => (
                                <li key={item.id} className="flex gap-4 py-4" data-testid={`cart-item-${item.id}`}>
                                    <img
                                        src={item.image}
                                        alt=""
                                        className="h-16 w-16 shrink-0 rounded-2xl border border-sand object-cover"
                                    />
                                    <div className="flex min-w-0 flex-1 flex-col">
                                        <p className="truncate text-sm font-bold text-forest">{item.name}</p>
                                        <p className="text-xs text-graphite-muted">{formatBRL(item.price)} / un.</p>
                                        <div className="mt-2 flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-1 rounded-full border border-sand bg-cream-card px-1">
                                                <button
                                                    type="button"
                                                    data-testid={`cart-qty-minus-${item.id}`}
                                                    aria-label={`Diminuir quantidade de ${item.name}`}
                                                    onClick={() => setQty(item.id, item.qty - 1)}
                                                    className="grid h-7 w-7 place-items-center rounded-full text-forest transition-colors hover:bg-coral/20"
                                                >
                                                    <Minus className="h-3.5 w-3.5" />
                                                </button>
                                                <span
                                                    className="min-w-6 text-center text-sm font-bold text-forest"
                                                    data-testid={`cart-qty-${item.id}`}
                                                >
                                                    {item.qty}
                                                </span>
                                                <button
                                                    type="button"
                                                    data-testid={`cart-qty-plus-${item.id}`}
                                                    aria-label={`Aumentar quantidade de ${item.name}`}
                                                    onClick={() => setQty(item.id, item.qty + 1)}
                                                    className="grid h-7 w-7 place-items-center rounded-full text-forest transition-colors hover:bg-coral/20"
                                                >
                                                    <Plus className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                            <span className="text-sm font-extrabold text-forest">
                                                {formatBRL(item.price * item.qty)}
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        data-testid={`cart-remove-${item.id}`}
                                        aria-label={`Remover ${item.name}`}
                                        onClick={() => remove(item.id)}
                                        className="self-start rounded-full p-1.5 text-graphite-muted/60 transition-colors hover:bg-coral/15 hover:text-coral-dark"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </li>
                            ))}
                        </ul>

                        <div className="border-t border-sand/70 bg-cream-card px-6 py-5">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-bold text-forest">Subtotal</span>
                                <span
                                    className="text-lg font-extrabold text-forest"
                                    data-testid="cart-subtotal"
                                >
                                    {formatBRL(subtotal)}
                                </span>
                            </div>
                            <p className="mt-1 text-[11px] text-graphite-muted">
                                Valores fictícios · frete e pagamento fora do escopo desta demonstração.
                            </p>
                            <button
                                type="button"
                                data-testid="cart-checkout-button"
                                onClick={checkout}
                                className="mt-4 w-full rounded-full bg-forest py-3.5 text-sm font-bold text-cream transition-all duration-300 hover:bg-forest-light hover:shadow-[0_14px_28px_rgba(23,61,53,0.25)]"
                            >
                                Finalizar compra (demo)
                            </button>
                            <button
                                type="button"
                                data-testid="cart-clear-button"
                                onClick={clear}
                                className="mt-2 w-full rounded-full border border-sand py-3 text-xs font-bold text-graphite-muted transition-colors hover:border-coral hover:text-coral-dark"
                            >
                                Esvaziar cesta
                            </button>
                        </div>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
}
